<template>
  <v-layout :column="column" class="app-text-field-container">
    <v-flex class="label-flex" :class="labelClass">
      <v-card-text class="app-input-label">
        <v-layout class="align-center">
          <v-flex grow>
            {{ labelLocalize ? $t(inputLabel): inputLabel }}
            <span v-show="required && !!inputLabel">*</span>
          </v-flex>
          <v-flex shrink>
            <slot name="label-append"></slot>
          </v-flex>
        </v-layout>
      </v-card-text>
    </v-flex>
    <v-flex class="input-flex" :class="inputClass" v-bind="inputFlexAttrs">
      <v-text-field :ref="id" :type="inputType" :value="value" v-bind="$attrs" :class="[defaultClass, isIE ? 'ie' : '']"
        :rules="validationRules" :validate-on-blur="validateOnBlur" v-mask="vmask" :id="id" :clearable="isClearable"
        @input="input($event, 'input')"
        @change="input($event, 'change')"
        @click="$emit('click', $event)"
        @click:clear="$emit('click:clear', $event)"
        @click:append="$emit('click:append', $event)"
        @click:append-outer="$emit('click:append-outer', $event)"
        @click:prepend="$emit('click:prepend', $event)"
        @blur="$emit('blur', $event)"
        @focus="$emit('focus', $event)"
        @keydown="$emit('keydown', $event)">
        <template v-slot:append>
          <slot name="append"></slot>
        </template>
        <template v-slot:append-outer>
          <slot name="append-outer"></slot>
        </template>
        <template v-slot:prepend-inner>
          <slot name="prepend-inner"></slot>
        </template>
        <template v-slot:prepend>
          <slot name="prepend"></slot>
        </template>
      </v-text-field>
      <slot name="under"></slot>
    </v-flex>
  </v-layout>
</template>

<script>
import { actions as _act } from '@/models/store.actions';
import { getLocale } from '@/helpers/locale-helper';
import get from 'lodash/get';
import { Guid } from 'guid-typescript';

export default {
  name: 'AppTextField',
  inheritAttrs: false,
  inject: ['isIE'],
  data: function() {
    return {
      column: true,
      id: Guid.raw(),
      inputFlexAttrs: {},
      inputLabel: '',
      inputType: '',
      maxLength: 0,
      row: false,
      rulesPreset: {
        email: v =>
          !v || /^[a-zA-Z0-9.!#$%&’*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(v) || getLocale('validate.correctEmail'),
        maxLength: v => !this.maxLength || !v || v.length <= this.maxLength || getLocale('validate.maxLength', { len: this.maxLength }),
        phone: v => !v || v.replace(/\D/g, '').length === 9 || getLocale('validate.wrongphone'),
        pwd: v => !v || v.length >= this.passwordLength || getLocale('validate.passlenght', { len: this.passwordLength }),
        required: v => !this.required || !!v || getLocale('validate.exist'),
        edrpouRule: v => !v || (v.length >= 8 && v.length <= 16) || getLocale('validate.edrpoLength', { min: 8, max: 16 })
      },
      validationRules: [],
      vmask: null
    };
  },
  props: {
    inputClass: { default: '' },
    label: { default: '' },
    labelClass: { default: '' },
    rules: undefined,
    type: { default: '' },
    mask: { default: '' },
    value: { default: '' },
    defaultClass: { default: 'app-text-field' },
    labelLocalize: { default: true },
    minPasswordLen: { default: undefined },
    forceShowClearBtn: { type: Boolean, default: false }
  },
  created() {
    if ('row' in this.$attrs) {
      this.column = false;
      this.row = true;
    }
    let sizes = Object.keys(this.$attrs).filter(a => /^xs|sm|md|lg|xl/.test(a));
    sizes.forEach(s => (this.inputFlexAttrs[s] = ''));

    this.validationRules.push(this.rulesPreset.required);

    if ('counter' in this.$attrs) {
      this.maxLength = Number(this.$attrs.counter);
    }
    if (this.maxLength && !isNaN(this.maxLength)) {
      this.validationRules.push(this.rulesPreset.maxLength);
    }
    this.vmask = { mask: '', id: this.id, component: this };
    this.inputType = this.type || 'text';
    this.inputLabel = this.label;
    switch (this.type) {
      case 'email':
        this.validationRules.push(this.rulesPreset.email);
        this.inputLabel = this.label === undefined ? 'common.email' : this.label;
        if (this.applyMask) {
          this.inputType = 'text';
          this.vmask = Object.assign(this.vmask, this.parseMask(this.mask, 'email'));
        }
        break;
      case 'password':
        if (!('disable-password-validate' in this.$attrs)) {
          this.validationRules.push(this.rulesPreset.pwd);
        }
        this.inputLabel = this.label === undefined ? 'common.password' : this.label;
        this.$watch('type', val => (val ? (this.inputType = val) : ''));
        break;
      case 'phone':
        if (!('disable-phone-validate' in this.$attrs)) {
          this.validationRules.push(this.rulesPreset.phone);
        }
        this.inputLabel = this.label === undefined ? 'common.phone' : this.label;
        if (this.applyMask) {
          this.inputType = 'text';
          this.vmask = Object.assign(this.vmask, this.parseMask(this.mask, 'phone'));
        }
        break;
      case 'integer':
        this.inputType = 'text';
        this.vmask = Object.assign(this.vmask, this.parseMask(this.mask, 'integer'));
        if ('positive-only' in this.$attrs) {
          this.vmask = Object.assign(this.vmask, { allowMinus: false });
        }
        break;
      case 'decimal':
        this.inputType = 'text';
        this.vmask = Object.assign(this.vmask, this.parseMask(this.mask, 'decimal'));
        break;
      case 'date':
        this.inputType = 'text';
        this.vmask = Object.assign(this.vmask, this.parseMask(this.mask, 'date'));
        break;
      case 'time':
        this.inputType = 'text';
        this.vmask = Object.assign(this.vmask, this.parseMask(this.mask, 'time'));
        break;
      case 'edrpou':
        this.inputType = 'text';
        this.validationRules.push(this.rulesPreset.edrpouRule);
        break;
      default:
        break;
    }
    this.validationRules = this.validationRules.concat(this.$props.rules || []);
  },
  mounted() {
    if ('force-number' in this.$attrs && this.type === 'integer' && !isNaN(+this.value)) {
      this.input(this.value, 'input');
    }
  },
  computed: {
    applyMask() {
      return !('no-auto-mask' in this.$attrs);
    },
    appSettings() {
      return this.$store.getters[_act.SETT_GET_APPSET];
    },
    defaultMinPassLen() {
      return get(this.appSettings, 'passwordOptions.requiredLength') || 0;
    },
    passwordLength() {
      let len;
      if (this.minPasswordLen !== undefined && !isNaN(this.minPasswordLen)) {
        len = +this.minPasswordLen;
      } else {
        len = +this.defaultMinPassLen;
      }
      return len || 0;
    },
    required() {
      return 'required' in this.$attrs && this.$attrs.required !== false;
    },
    validateOnBlur() {
      return !('validate-on-blur' in this.$attrs) || !!this.$attrs['validate-on-blur'];
    },
    readonly() {
      return 'readonly' in this.$attrs && this.$attrs.readonly !== false;
    },
    isClearable() {
      return (!this.readonly || this.forceShowClearBtn) && 'clearable' in this.$attrs && this.$attrs.clearable !== false;
    }
  },
  methods: {
    input(val, ev) {
      if (this.type === 'integer') {
        this.$emit(ev, parseInt(val));
      } else {
        this.$emit(ev, val);
      }
    },
    parseMask(inputMask, defaultMask) {
      let outputMask;
      if (inputMask) {
        if (typeof inputMask === 'string') {
          outputMask = { mask: inputMask };
        } else {
          outputMask = Object.assign({}, inputMask);
        }
      } else {
        if (typeof defaultMask === 'string') {
          outputMask = { mask: defaultMask };
        } else {
          outputMask = Object.assign({}, defaultMask);
        }
      }
      return outputMask || {};
    },
    focus() {
      this.$refs[this.id].focus();
    }
  }
};
</script>
